import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { EmployeesTab } from "./people/EmployeesTab";
import { AttendanceTab } from "./people/AttendanceTab";
import { LeavesTab } from "./people/LeavesTab";
import { PayrollTab } from "./people/PayrollTab";
import { OnboardingTab } from "./people/OnboardingTab";
import { ReimbursementsTab } from "./people/ReimbursementsTab";
import { PerformanceTab } from "./people/PerformanceTab";
import { BgvTab } from "./people/BgvTab";
import { AssetsTab } from "./people/AssetsTab";
import { ExitTab } from "./people/ExitTab";
import { CalendarTab } from "./people/CalendarTab";

export default function People() {
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [onboarding, setOnboarding] = useState([]);
  const [claims, setClaims] = useState([]);
  const [goals, setGoals] = useState([]);
  const [bgvList, setBgvList] = useState([]);
  const [assets, setAssets] = useState([]);
  const [exits, setExits] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [hrmsGate, setHrmsGate] = useState(null);

  const load = () => {
    api.get("/employer/employees").then(({ data }) => setEmployees(data.employees)).catch((e) => {
      if (e.response?.status === 402) setHrmsGate(e.response?.data?.detail || "An HRMS plan is required.");
    });
    api.get("/employer/attendance").then(({ data }) => setAttendance(data.attendance)).catch(() => {});
    api.get("/employer/leaves").then(({ data }) => setLeaves(data.leaves)).catch(() => {});
    api.get("/employer/payroll").then(({ data }) => setPayslips(data.payslips)).catch(() => {});
    api.get("/employer/onboarding").then(({ data }) => setOnboarding(data.onboarding)).catch(() => {});
    api.get("/employer/reimbursements").then(({ data }) => setClaims(data.claims)).catch(() => {});
    api.get("/employer/goals").then(({ data }) => setGoals(data.goals)).catch(() => {});
    api.get("/employer/bgv").then(({ data }) => setBgvList(data.bgv)).catch(() => {});
    api.get("/employer/assets").then(({ data }) => setAssets(data.assets)).catch(() => {});
    api.get("/employer/exits").then(({ data }) => setExits(data.exits)).catch(() => {});
    api.get("/employer/holidays").then(({ data }) => setHolidays(data.holidays)).catch(() => {});
    api.get("/employer/shifts").then(({ data }) => setShifts(data.shifts)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="people-page">
      {hrmsGate ? (
        <div className="bg-white border border-border/60 rounded-xl p-10 text-center" data-testid="hrms-gate-notice">
          <Users className="w-10 h-10 text-[#2563EB] mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-lg text-[#0B1B3D]">People & HRMS requires an HRMS plan</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{hrmsGate}</p>
          <Link to="/employer/plans">
            <Button className="rounded-full bg-[#F5A623] text-[#0B1B3D] font-bold mt-5" data-testid="hrms-gate-cta">
              View HRMS Plans
            </Button>
          </Link>
        </div>
      ) : (
      <Tabs defaultValue="employees">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="tab-leaves">Leave Requests</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="reimbursements" data-testid="tab-reimbursements">Reimbursements</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="bgv" data-testid="tab-bgv">BGV</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets">Assets</TabsTrigger>
          <TabsTrigger value="exit" data-testid="tab-exit">Exit</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Holidays & Shifts</TabsTrigger>
        </TabsList>

        <EmployeesTab employees={employees} />
        <AttendanceTab attendance={attendance} />
        <LeavesTab leaves={leaves} onRefresh={load} />
        <PayrollTab payslips={payslips} employees={employees} onRefresh={load} />
        <OnboardingTab onboarding={onboarding} />
        <ReimbursementsTab claims={claims} onRefresh={load} />
        <PerformanceTab goals={goals} employees={employees} onRefresh={load} />
        <BgvTab bgvList={bgvList} onRefresh={load} />
        <AssetsTab assets={assets} employees={employees} onRefresh={load} />
        <ExitTab exits={exits} onRefresh={load} />
        <CalendarTab holidays={holidays} shifts={shifts} employees={employees} onRefresh={load} />
      </Tabs>
      )}
    </div>
  );
}
